import type { UploadFile } from '@pflegenavi/shared-frontend/platform';
import type {
  Gender,
  LineItemConfigurationResultDto,
  OnboardingControllerResultTypes,
  ServiceProviderControllerResultTypes,
  TaxCodeResultDto,
} from '@pflegenavi/service-provider-shared/api-endpoints';
import { ConnectionStatus } from '@pflegenavi/service-provider-shared/api-endpoints';
import { PaymentProcessor } from '@pflegenavi/shared/api';
import type { StoreApi } from 'zustand';
import { createStore, useStore } from 'zustand';
import type { FC, PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useMemo } from 'react';
import {
  useGetResidentListPhoenix,
  useGetTaxCodesByCountryPhoenix,
  useGetTaxLineConfigurationsPhoenix,
  useGetServiceProviderPhoenix,
  useUploadReceiptImage,
} from '@pflegenavi/service-provider-frontend/api';
import { v4 } from 'uuid';
import { computeMd5HashOfFile } from '@pflegenavi/shared-web/hash-utils';
import { useInitializeResidents } from '../../hooks/useUnitializeResidents';
import { useGetCountryOfServiceProvider } from '../../hooks/useGetCountryOfServiceProvider';
import type { ReceiptBatchFormValues } from './interfaces/ReceiptBatchFormValues';
import type { FormikContextType } from 'formik';
import { useGetGlobalServiceProviderId } from '../../hooks/useGetGlobalServiceProviderId';
import { useShallow } from 'zustand/react/shallow';

interface ReceiptBatchStore {
  uploadReceiptImage: UploadFile;
  residents: Array<{
    id: string;
    name: string;
    firstName: string;
    lastName: string;
    gender: Gender;
  }>;
  isLoading: boolean;
  taxConfigurations: TaxCodeResultDto[];
  lineItemConfigurations: LineItemConfigurationResultDto[];
  serviceProvider:
    | ServiceProviderControllerResultTypes['getServiceProvider']
    | undefined;
  formik: FormikContextType<ReceiptBatchFormValues> | undefined;
  setFormik: (formik: FormikContextType<ReceiptBatchFormValues>) => void;
  nursingHomes: OnboardingControllerResultTypes['getNursingHomeList'];
}

const ReceiptBatchContext = createContext<StoreApi<ReceiptBatchStore>>(
  undefined as any
);

export const ReceiptBatchProvider: FC<PropsWithChildren> = ({ children }) => {
  useInitializeResidents();

  const { globalServiceProviderId } = useGetGlobalServiceProviderId();

  const store = useMemo(
    () =>
      createStore<ReceiptBatchStore>((set) => ({
        uploadReceiptImage: () => {
          return Promise.resolve(undefined);
        },
        residents: [],
        isLoading: false,
        lineItemConfigurations: [],
        taxConfigurations: [],
        formik: undefined,
        serviceProvider: undefined,
        setFormik: (formik) => set({ formik }),
        nursingHomes: [],
      })),
    []
  );

  // Get service provider data which includes nursing home connections
  const { data: serviceProviderData } = useGetServiceProviderPhoenix();

  // Transform Phoenix nursing_home_connections to match legacy format
  const nursingHomes = useMemo(() => {
    return (serviceProviderData?.data.nursing_home_connections ?? []).map(
      (connection) => {
        // Convert string status to ConnectionStatus enum
        const statusMap: Record<string, ConnectionStatus> = {
          invite_pending: ConnectionStatus.InvitePending,
          active: ConnectionStatus.Active,
          inactive: ConnectionStatus.Inactive,
          invite_rejected: ConnectionStatus.InviteRejected,
        };
        return {
          nursingHomeId: connection.nursing_home_id,
          nursingHomeName: connection.nursing_home_name,
          tenant: connection.tenant,
          connectionStatus:
            statusMap[connection.connection_status] ??
            ConnectionStatus.Inactive,
          globalServiceProviderId: globalServiceProviderId,
          paymentProcessor:
            (connection.payment_processor as PaymentProcessor) ??
            PaymentProcessor.Stripe,
        };
      }
    );
  }, [
    serviceProviderData?.data.nursing_home_connections,
    globalServiceProviderId,
  ]);

  const [nursingHomeId] = useStore(
    store,
    useShallow((state) => [state.formik?.values?.nursingHomeId])
  );

  const selectedNursingHome = nursingHomes?.find(
    (n) => n.nursingHomeId === nursingHomeId
  );

  const { upload } = useUploadReceiptImage({
    tenant: selectedNursingHome?.tenant ?? '',
    uuid: v4,
    computeMd5Hash: computeMd5HashOfFile,
  });

  const { data: residents, isPending: isLoadingResidents } =
    useGetResidentListPhoenix(
      {
        nursingHomeId: selectedNursingHome?.nursingHomeId ?? '',
        globalServiceProviderId: globalServiceProviderId!,
      },
      {
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchInterval: 0,
        refetchIntervalInBackground: false,
        enabled:
          !!globalServiceProviderId && !!selectedNursingHome?.nursingHomeId,
      }
    );

  const countryId = useGetCountryOfServiceProvider();
  const { data: taxConfigurations, isPending: isLoadingTaxConfigurations } =
    useGetTaxCodesByCountryPhoenix(countryId);
  const {
    data: lineItemConfigurations,
    isLoading: isLoadingLineItemConfigurations,
  } = useGetTaxLineConfigurationsPhoenix(globalServiceProviderId);

  // Transform Phoenix service provider data to match legacy interface
  const serviceProvider = useMemo(() => {
    if (!serviceProviderData) {
      return undefined;
    }
    const data = serviceProviderData.data;
    return {
      id: data.global_service_provider_id,
      name: data.name,
      countryId: data.country_id ?? undefined,
      defaultAutoGenerateReceipts: data.default_auto_generate_receipts ?? false,
      defaultTaxCodeId: data.default_tax_code ?? undefined,
      ownedByPflegenavi: data.stripe_account?.owned_by_pflegenavi ?? false,
      stripeOperatedBy: data.stripe_account?.owned_by_pflegenavi
        ? 'pflegenavi'
        : data.payment_service_provider === 'stripe'
        ? 'service_provider'
        : 'none',
      paymentProcessor:
        data.payment_service_provider === 'stripe'
          ? 'stripe'
          : data.payment_service_provider === 'mangopay'
          ? 'mangopay'
          : 'mangopay',
      mangopayScaEnrolled: data.mangopay_sca_enrolled,
      mangopay_user_id: data.mangopay_user_id,
      legalPersonType: data.legal_person_type,
      nursing_home_connections: data.nursing_home_connections,
    } as ServiceProviderControllerResultTypes['getServiceProvider'];
  }, [serviceProviderData]);

  useEffect(() => {
    store.setState({
      isLoading:
        isLoadingResidents ||
        isLoadingTaxConfigurations ||
        isLoadingLineItemConfigurations,
    });
  }, [
    isLoadingLineItemConfigurations,
    isLoadingResidents,
    isLoadingTaxConfigurations,
    store,
  ]);

  useEffect(() => {
    const transformed = (residents ?? []).map((resident) => ({
      id: resident.id,
      name: `${resident.firstName} ${resident.lastName}`,
      firstName: resident.firstName,
      lastName: resident.lastName,
      gender: resident.gender as Gender,
    }));
    store.setState({ residents: transformed });
  }, [residents, store]);

  useEffect(() => {
    store.setState({ uploadReceiptImage: upload });
  }, [upload, store]);

  useEffect(() => {
    store.setState({ taxConfigurations: taxConfigurations ?? [] });
  }, [taxConfigurations, store]);

  useEffect(() => {
    store.setState({ lineItemConfigurations: lineItemConfigurations ?? [] });
  }, [lineItemConfigurations, store]);

  useEffect(() => {
    if (serviceProvider) {
      store.setState({ serviceProvider });
    }
  }, [serviceProvider, store]);

  useEffect(() => {
    const activeNursingHomes = (nursingHomes ?? []).filter(
      (n) => n.connectionStatus === ConnectionStatus.Active
    );

    store.setState({ nursingHomes: activeNursingHomes });
  }, [nursingHomes, store]);

  return (
    <ReceiptBatchContext.Provider value={store}>
      {children}
    </ReceiptBatchContext.Provider>
  );
};

export function useReceiptBatchStore<T>(
  selector: (state: ReceiptBatchStore) => T
): T {
  const store = useContext(ReceiptBatchContext);
  return useStore(store, selector);
}
